import type { HttpClient } from '../http/types';
import type {
  ActivateProjectPayload,
  BackendProject,
  BackendProjectListPage,
  BackendProjectManager,
  BackendProjectMember,
  BackendProjectRoleCode,
  ChangeProjectTemperaturePayload,
  CreateBackendProjectPayload,
  ProjectListTab,
  ProjectDiscussion,
  ProjectMyAccess,
  PendingPortfolioAccess,
  ProjectStatusReport,
  ProjectStatusReportPage,
  ProjectTemperatureHistoryPage,
} from '../types/project';
import type { BackendWorkItemComment } from '../types/work-item';
import { ENDPOINTS } from './endpoints';

export interface ProjectListParams {
  tab?: ProjectListTab;
  limit?: number;
  cursor?: string;
  search?: string;
}

export function createProjectsApi(http: HttpClient) {
  return {
    /** GET /bff/projects — cursor-based e limitado aos projetos visíveis ao usuário autenticado. */
    list: (params?: ProjectListParams) =>
      http.get<BackendProjectListPage>(ENDPOINTS.projects.root, { query: { ...params } }),

    /** POST /bff/projects real — cria projeto pendente de aprovação admin. */
    createBackend: (payload: CreateBackendProjectPayload) =>
      http.post<void>(ENDPOINTS.projects.root, payload),

    listPendingAdminApproval: () =>
      http.get<BackendProject[]>(ENDPOINTS.projects.pendingAdminApproval),

    /** Colaboradores ativos cujo cargo atual é `PMO | GP`. */
    projectManagers: () => http.get<BackendProjectManager[]>(ENDPOINTS.projects.projectManagers),

    pendingPortfolioAccess: () =>
      http.get<PendingPortfolioAccess>(ENDPOINTS.projects.pendingPortfolioAccess),

    /** GET /bff/projects/:id — admin ou participante do projeto. */
    getById: (id: string) => http.get<BackendProject>(ENDPOINTS.projects.byId(id)),

    update: (id: string, payload: { name: string; areaIds: readonly string[] }) =>
      http.patch<void>(ENDPOINTS.projects.update(id), payload),

    changeResponsibleProjectManager: (id: string, newResponsibleProjectManagerId: string) =>
      http.patch<void>(ENDPOINTS.projects.responsibleProjectManager(id), {
        newResponsibleProjectManagerId,
      }),

    /** POST /bff/projects/:id/activate — metodologia, sprint (ágil) e cronograma. */
    activate: (id: string, payload: ActivateProjectPayload) =>
      http.post<void>(ENDPOINTS.projects.activate(id), payload),

    /** PATCH /bff/projects/:id/schedule — cronograma administrativo; em ágil completa as sprints. */
    updateSchedule: (id: string, payload: { startDate?: string; estimatedEndDate: string }) =>
      http.patch<void>(ENDPOINTS.projects.schedule(id), payload),

    /** POST /bff/projects/:id/request-signature — envia um projeto ativo para assinatura. */
    requestSignature: (id: string) => http.post<void>(ENDPOINTS.projects.requestSignature(id)),

    /** POST /bff/projects/:id/reopen — devolve um projeto em assinatura para correção. */
    reopen: (id: string) => http.post<void>(ENDPOINTS.projects.reopen(id)),

    /** PATCH /bff/projects/:id/milestones — datas opcionais de planejamento. */
    updateMilestones: (
      id: string,
      payload: {
        plannedEndDate?: string | null;
        handoverDate?: string | null;
        kickoffDate?: string | null;
      },
    ) => http.patch<void>(ENDPOINTS.projects.milestones(id), payload),

    /** PUT /bff/projects/:id/temperature — nível + motivo obrigatório. */
    changeTemperature: (id: string, payload: ChangeProjectTemperaturePayload) =>
      http.put<void>(ENDPOINTS.projects.temperature(id), payload),

    /** GET /bff/projects/:id/temperature/history — alterações mais recentes primeiro. */
    temperatureHistory: (id: string, params?: { limit?: number; cursor?: string }) =>
      http.get<ProjectTemperatureHistoryPage>(ENDPOINTS.projects.temperatureHistory(id), {
        query: { ...params },
      }),

    listStatusReports: (id: string, params?: { limit?: number; cursor?: string }) =>
      http.get<ProjectStatusReportPage>(ENDPOINTS.projects.statusReports(id), {
        query: { ...params },
      }),

    createStatusReport: (id: string, content: string) =>
      http.post<ProjectStatusReport>(ENDPOINTS.projects.statusReports(id), { content }),

    updateStatusReport: (id: string, reportId: string, content: string) =>
      http.patch<void>(ENDPOINTS.projects.statusReportById(id, reportId), { content }),

    deleteStatusReport: (id: string, reportId: string) =>
      http.delete<void>(ENDPOINTS.projects.statusReportById(id, reportId)),

    listDiscussions: (id: string) =>
      http.get<ProjectDiscussion[]>(ENDPOINTS.projects.discussions(id)),

    createDiscussion: (id: string, payload: { name: string; description?: string | null }) =>
      http.post<ProjectDiscussion>(ENDPOINTS.projects.discussions(id), payload),

    updateDiscussion: (
      id: string,
      discussionId: string,
      payload: { name?: string; description?: string | null },
    ) =>
      http.patch<ProjectDiscussion>(ENDPOINTS.projects.discussionById(id, discussionId), payload),

    deleteDiscussion: (id: string, discussionId: string) =>
      http.delete<void>(ENDPOINTS.projects.discussionById(id, discussionId)),

    listDiscussionComments: (id: string, discussionId: string) =>
      http.get<BackendWorkItemComment[]>(ENDPOINTS.projects.discussionComments(id, discussionId)),

    createDiscussionComment: (id: string, discussionId: string, content: string) =>
      http.post<BackendWorkItemComment>(ENDPOINTS.projects.discussionComments(id, discussionId), {
        content,
      }),

    pause: (id: string) => http.post<void>(ENDPOINTS.projects.pause(id)),

    resume: (id: string) => http.post<void>(ENDPOINTS.projects.resume(id)),

    complete: (id: string) => http.post<void>(ENDPOINTS.projects.complete(id)),

    audit: (id: string) => http.post<void>(ENDPOINTS.projects.audit(id)),

    archive: (id: string) => http.post<void>(ENDPOINTS.projects.archive(id)),

    approve: (id: string) => http.post<void>(ENDPOINTS.projects.approve(id)),

    reject: (id: string, justification: string) =>
      http.post<void>(ENDPOINTS.projects.reject(id), { justification }),

    members: (id: string) => http.get<BackendProjectMember[]>(ENDPOINTS.projects.members(id)),

    myAccess: (id: string) => http.get<ProjectMyAccess>(ENDPOINTS.projects.myAccess(id)),

    addMember: (id: string, userId: string, roleCode?: BackendProjectRoleCode) =>
      http.post<void>(ENDPOINTS.projects.members(id), { userId, roleCode }),

    changeMemberRole: (id: string, userId: string, roleCode: BackendProjectRoleCode) =>
      http.patch<void>(`${ENDPOINTS.projects.members(id)}/${userId}`, { roleCode }),

    removeMember: (id: string, userId: string) =>
      http.delete<void>(`${ENDPOINTS.projects.members(id)}/${userId}`),
  };
}

export type ProjectsApi = ReturnType<typeof createProjectsApi>;
